import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaFlask, FaTimes } from 'react-icons/fa';
import { FiLoader, FiAlertCircle } from 'react-icons/fi';
import { useIngredientAutocomplete } from '../../hooks/useIngredientAutocomplete';

/**
 * Multi-select autocomplete input for selecting ingredients.
 * Displays selected ingredients as removable chips/tags.
 * 
 * @param {Object} props
 * @param {Array<{uri: string, name: string}>} props.selectedIngredients - Currently selected ingredients
 * @param {Function} props.onSelect - Callback when an ingredient is selected
 * @param {Function} props.onRemove - Callback when an ingredient is removed (receives uri)
 * @param {number} props.maxSelections - Maximum number of selections allowed (default 10)
 * @param {boolean} props.disabled - Whether the selector is disabled
 */
export default function IngredientSelector({
  selectedIngredients = [],
  onSelect,
  onRemove,
  maxSelections = 10,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const {
    suggestions,
    isLoading,
    error,
    search,
    clear,
  } = useIngredientAutocomplete();

  const isMaxReached = selectedIngredients.length >= maxSelections;
  const isInputDisabled = disabled || isMaxReached;

  // Filter out already selected ingredients from suggestions
  const filteredSuggestions = suggestions.filter(
    (suggestion) => !selectedIngredients.some((selected) => selected.uri === suggestion.uri)
  );

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (value.length >= 3) {
      search(value);
      setIsDropdownOpen(true);
      setActiveIndex(-1);
    } else {
      setIsDropdownOpen(false);
      clear();
    }
  }, [search, clear]);

  // Handle ingredient selection
  const handleSelect = useCallback((ingredient) => {
    // Check for duplicates (shouldn't happen due to filtering, but extra safety)
    const isDuplicate = selectedIngredients.some(
      (selected) => selected.uri === ingredient.uri
    );
    
    if (!isDuplicate) {
      onSelect(ingredient);
    }
    
    // Clear input and close dropdown
    setInputValue('');
    setIsDropdownOpen(false);
    clear();
    setActiveIndex(-1);
    
    // Refocus input if not at max
    if (selectedIngredients.length + 1 < maxSelections) {
      inputRef.current?.focus();
    }
  }, [selectedIngredients, onSelect, clear, maxSelections]);

  // Handle ingredient removal
  const handleRemove = useCallback((uri) => {
    onRemove(uri);
    inputRef.current?.focus();
  }, [onRemove]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isDropdownOpen || filteredSuggestions.length === 0) {
      // Handle backspace to remove last tag when input is empty
      if (e.key === 'Backspace' && inputValue === '' && selectedIngredients.length > 0) {
        const lastIngredient = selectedIngredients[selectedIngredients.length - 1];
        handleRemove(lastIngredient.uri);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredSuggestions.length) {
          handleSelect(filteredSuggestions[activeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  }, [isDropdownOpen, filteredSuggestions, activeIndex, handleSelect, inputValue, selectedIngredients, handleRemove]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && dropdownRef.current) {
      const activeElement = dropdownRef.current.children[activeIndex];
      if (activeElement) {
        activeElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [activeIndex]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
        setActiveIndex(-1);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle focus to reopen dropdown if there's a valid query
  const handleFocus = useCallback(() => {
    if (inputValue.length >= 3 && !isMaxReached) {
      setIsDropdownOpen(true);
    }
  }, [inputValue, isMaxReached]);

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input container with selected tags */}
      <div
        className={`flex flex-wrap items-center gap-2 p-3 min-h-[56px] rounded-xl bg-white/60 border transition-all duration-200 ${
          isInputDisabled
            ? 'border-secondary/20 bg-secondary/5 cursor-not-allowed'
            : 'border-white/80 hover:border-secondary/30 focus-within:border-emerald-600/40 focus-within:ring-2 focus-within:ring-emerald-600/10'
        }`}
        onClick={() => !isInputDisabled && inputRef.current?.focus()}
      >
        {/* Selected ingredient tags */}
        {selectedIngredients.map((ingredient) => (
          <span
            key={ingredient.uri}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-14 font-medium transition-colors group"
          >
            <FaFlask className="size-3 text-emerald-600/70" />
            <span className="max-w-[150px] truncate">{ingredient.name}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(ingredient.uri);
              }}
              className="p-0.5 -mr-1 rounded-full hover:bg-emerald-200 transition-colors"
              aria-label={`Remove ${ingredient.name}`}
              disabled={disabled}
            >
              <FaTimes className="size-3 text-emerald-600 hover:text-emerald-800" />
            </button>
          </span>
        ))}

        {/* Input field */}
        {!isMaxReached ? (
          <div className="relative flex-1 min-w-[150px] flex items-center">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              disabled={isInputDisabled}
              placeholder={
                selectedIngredients.length === 0
                  ? 'Type to search ingredients...'
                  : 'Add another ingredient...'
              }
              className="w-full bg-transparent border-none outline-none text-16 text-secondary placeholder:text-secondary/50 disabled:cursor-not-allowed"
              aria-label="Search ingredients"
              aria-expanded={isDropdownOpen}
              aria-autocomplete="list"
              aria-controls="ingredient-suggestions"
              role="combobox"
            />
            
            {/* Inline loading spinner */}
            {isLoading && (
              <FiLoader className="absolute right-2 size-5 text-emerald-600 animate-spin" />
            )}
          </div>
        ) : (
          <span className="text-14 text-secondary/60 italic">
            Maximum {maxSelections} ingredients reached
          </span>
        )}
      </div>

      {/* Inline error message */}
      {error && (
        <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-14">
          <FiAlertCircle className="size-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Autocomplete dropdown */}
      {isDropdownOpen && !error && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          id="ingredient-suggestions"
          role="listbox"
          className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-secondary/10 rounded-xl shadow-xl shadow-secondary/8 z-50 max-h-[300px] overflow-y-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={suggestion.uri}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                index === activeIndex
                  ? 'bg-emerald-50'
                  : 'hover:bg-secondary/5'
              } ${index > 0 ? 'border-t border-secondary/5' : ''}`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex-shrink-0">
                <FaFlask className="size-3.5" />
              </div>
              <span className="text-16 font-text text-secondary">
                {suggestion.name}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {isDropdownOpen && !error && !isLoading && inputValue.length >= 3 && filteredSuggestions.length === 0 && suggestions.length === 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-secondary/10 rounded-xl shadow-xl shadow-secondary/8 z-50 px-4 py-6 text-center"
        >
          <span className="text-14 text-secondary/60">
            No ingredients found matching "{inputValue}"
          </span>
        </div>
      )}

      {/* All suggestions already selected message */}
      {isDropdownOpen && !error && !isLoading && inputValue.length >= 3 && suggestions.length > 0 && filteredSuggestions.length === 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-secondary/10 rounded-xl shadow-xl shadow-secondary/8 z-50 px-4 py-6 text-center"
        >
          <span className="text-14 text-secondary/60">
            All matching ingredients are already selected
          </span>
        </div>
      )}
    </div>
  );
}
